// Pushes locally-saved documents to the server when there is a connection.

import documentService from '../services/documentService';
import { hasValidToken } from './tokenUtils';
import { toBase64Payload } from '../components/documents/documentImages';
import { compressImage, needsConversion } from './imageCompression';
import {
  SYNC_FAILED,
  SYNC_PENDING,
  SYNC_SYNCING,
  deleteDraft,
  getDraftImages,
  getDrafts,
  patchDraft,
} from './documentDraftStore';

export const OUTBOX_EVENT = 'document-outbox-changed';

const BACKOFF_MS = [15000, 30000, 60000, 300000];

let running = false;
let timer = null;
let started = false;

const announce = () => window.dispatchEvent(new CustomEvent(OUTBOX_EVENT));

export const subscribeToOutbox = (listener) => {
  window.addEventListener(OUTBOX_EVENT, listener);
  return () => window.removeEventListener(OUTBOX_EVENT, listener);
};

const classify = (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) return 'unauthenticated';

  // apiService.ensureAuthenticated throws a plain Error before any request.
  if (!error?.response && !error?.request && /authentication token/i.test(error?.message ?? '')) {
    return 'unauthenticated';
  }

  // 408 and 429 are the server asking us to come back, not a rejection.
  if (status >= 400 && status < 500 && status !== 408 && status !== 429) return 'permanent';

  return 'transient';
};

export const isRetryableError = (error) => classify(error) !== 'permanent';

const serverMessage = (error) => {
  const data = error?.response?.data;
  const fieldErrors = data?.errors ? Object.values(data.errors).flat() : [];
  return fieldErrors[0] || data?.message || error?.message || 'Upload failed';
};

const buildImagePayload = async (draftId) => {
  const stored = await getDraftImages(draftId);
  const byField = {};

  for (const image of stored) {
    let file = new File([image.blob], image.name, { type: image.type });

    if (needsConversion(file)) {
      try {
        file = await compressImage(file);
      } catch {
        // Keep the original.
      }
    }

    (byField[image.fieldKey] ??= []).push({ originFileObj: file });
  }

  return toBase64Payload(byField);
};

const send = async (draft) => {
  const images = await buildImagePayload(draft.draftId);

  if (draft.documentId) {
    await documentService.updateDocument(draft.documentId, {
      name: draft.name,
      documentTemplateStatusId: draft.statusId ?? null,
      fieldValues: draft.fieldValues ?? [],
      images,
    });
    return;
  }

  await documentService.createDocument({
    clientRequestId: draft.draftId,
    documentTemplateId: draft.templateId,
    name: draft.name,
    companyId: draft.companyId,
    relationId: draft.relationId,
    documentTemplateStatusId: draft.statusId ?? null,
    fieldValues: draft.fieldValues ?? [],
    images,
  });
};

const schedule = (attempts) => {
  if (timer) clearTimeout(timer);
  const delay = BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)];
  timer = setTimeout(() => {
    timer = null;
    flushOutbox();
  }, delay);
};

export const flushOutbox = async () => {
  if (running) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  if (!hasValidToken()) return;

  running = true;

  try {
    const queued = (await getDrafts()).filter(
      (draft) => draft.syncState === SYNC_PENDING || draft.syncState === SYNC_SYNCING
    );

    if (queued.length === 0) return;

    let retryAfter = null;

    for (const draft of queued) {
      await patchDraft(draft.draftId, { syncState: SYNC_SYNCING });
      announce();

      try {
        await send(draft);
        await deleteDraft(draft.draftId);
      } catch (error) {
        const kind = classify(error);

        if (kind === 'permanent') {
          await patchDraft(draft.draftId, {
            syncState: SYNC_FAILED,
            lastError: serverMessage(error),
            lastAttemptOn: Date.now(),
          });
        } else {
          const attemptCount = (draft.attemptCount ?? 0) + 1;

          await patchDraft(draft.draftId, {
            syncState: SYNC_PENDING,
            attemptCount,
            lastError: kind === 'unauthenticated' ? 'Waiting until you sign in again' : null,
            lastAttemptOn: Date.now(),
          });

          retryAfter = Math.max(retryAfter ?? 0, attemptCount);

          if (kind === 'unauthenticated' || !error?.response) break;
        }
      }

      announce();
    }

    if (retryAfter !== null) schedule(retryAfter);
  } catch (error) {
    console.error('Document outbox failed', error);
  } finally {
    running = false;
    announce();
  }
};

/**
 * Hand a draft to the outbox. Returns once it is queued, not once it is sent.
 */
export const enqueueDraft = async (draftId) => {
  await patchDraft(draftId, {
    syncState: SYNC_PENDING,
    attemptCount: 0,
    lastError: null,
  });

  announce();
  flushOutbox();
};

export const startOutbox = () => {
  if (started) return;
  started = true;

  window.addEventListener('online', () => flushOutbox());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') flushOutbox();
  });

  flushOutbox();
};
